import { create } from 'zustand';
import type { SmeCompanyView, User } from '@shared/contracts/entities';
import type { EntitlementContext } from '@/services/entitlements';
import { db } from '@/mocks/db';
import { nowIso } from '@/services/clock';
import { scaStale, sessionExpired, type SessionState } from '@/mocks/api';

/**
 * Session and company context.
 *
 * Holds only identity and selection. All entitlement decisions are derived
 * from this plus the mock database, so navigation and route guards can never
 * disagree with what the adapter enforces.
 */

export type AuthPhase = 'signed-out' | 'awaiting-sca' | 'awaiting-company' | 'ready';

/**
 * Session survives a page refresh via sessionStorage.
 *
 * baseline_handoff/app/02_APPLICATION_ARCHITECTURE.md lists browser persistence
 * as part of the architecture. Only non-sensitive identity and timestamps are
 * stored — never a credential, token or any account data — and the deterministic
 * clock still expires the session, so persistence cannot outlive its validity.
 *
 * sessionStorage, not localStorage: the session must not survive closing the
 * tab, and localStorage is reserved for the layout recovery draft.
 */
const SESSION_KEY = 'sme-mobile:session';

type PersistedSession = {
  readonly userId: string;
  readonly companyId: string | null;
  readonly scaSatisfiedAt: string | null;
  readonly startedAt: string;
  readonly phase: AuthPhase;
};

function readPersisted(): PersistedSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSession;
    // Only a fully established session is restorable; a half-finished SCA
    // challenge is deliberately not resumed after a reload.
    if (parsed.phase !== 'ready' && parsed.phase !== 'awaiting-company') return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePersisted(value: PersistedSession | null): void {
  try {
    if (value === null) sessionStorage.removeItem(SESSION_KEY);
    else sessionStorage.setItem(SESSION_KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable (private mode). The session simply will not survive
    // a refresh, which is safe.
  }
}

type SessionStore = {
  phase: AuthPhase;
  userId: string | null;
  companyId: string | null;
  scaSatisfiedAt: string | null;
  startedAt: string | null;
  pendingChallengeId: string | null;
  /** Route the user was heading to before being interrupted by auth. */
  returnTo: string | null;

  beginSca: (userId: string, challengeId: string, returnTo?: string | null) => void;
  completeSca: () => void;
  selectCompany: (companyId: string) => void;
  refreshSca: () => void;
  signOut: () => void;
};

const restored = readPersisted();

export const useSession = create<SessionStore>((set, get) => ({
  phase: restored?.phase ?? 'signed-out',
  userId: restored?.userId ?? null,
  companyId: restored?.companyId ?? null,
  scaSatisfiedAt: restored?.scaSatisfiedAt ?? null,
  startedAt: restored?.startedAt ?? null,
  pendingChallengeId: null,
  returnTo: null,

  beginSca: (userId, challengeId, returnTo = null) =>
    set({
      phase: 'awaiting-sca',
      userId,
      pendingChallengeId: challengeId,
      returnTo,
      startedAt: nowIso(),
    }),

  completeSca: () => {
    const { userId } = get();
    if (!userId) return;
    const user = db().users.find((u) => u.id === userId);
    const companies = db().companies.filter((c) => user?.companyIds.includes(c.id));
    const only = companies.length === 1 ? companies[0] : undefined;
    const next = {
      phase: (only ? 'ready' : 'awaiting-company') as AuthPhase,
      companyId: only ? only.id : null,
      scaSatisfiedAt: nowIso(),
      pendingChallengeId: null,
      startedAt: nowIso(),
    };
    set(next);
    writePersisted({
      userId,
      companyId: next.companyId,
      scaSatisfiedAt: next.scaSatisfiedAt,
      startedAt: next.startedAt,
      phase: next.phase,
    });
  },

  selectCompany: (companyId) => {
    set({ companyId, phase: 'ready' });
    const s = get();
    if (s.userId && s.startedAt) {
      writePersisted({
        userId: s.userId,
        companyId,
        scaSatisfiedAt: s.scaSatisfiedAt,
        startedAt: s.startedAt,
        phase: 'ready',
      });
    }
  },

  refreshSca: () => {
    set({ scaSatisfiedAt: nowIso() });
    const s = get();
    if (s.userId && s.startedAt) {
      writePersisted({
        userId: s.userId,
        companyId: s.companyId,
        scaSatisfiedAt: s.scaSatisfiedAt,
        startedAt: s.startedAt,
        phase: s.phase,
      });
    }
  },

  signOut: () => {
    writePersisted(null);
    set({
      phase: 'signed-out',
      userId: null,
      companyId: null,
      scaSatisfiedAt: null,
      startedAt: null,
      pendingChallengeId: null,
      returnTo: null,
    });
  },
}));

/** Snapshot suitable for the adapter's session helpers. */
export function sessionSnapshot(): SessionState | null {
  const s = useSession.getState();
  if (!s.userId || !s.companyId || !s.startedAt) return null;
  return {
    userId: s.userId,
    companyId: s.companyId,
    scaSatisfiedAt: s.scaSatisfiedAt,
    startedAt: s.startedAt,
  };
}

export function isSessionExpired(): boolean {
  const snapshot = sessionSnapshot();
  return snapshot ? sessionExpired(snapshot) : false;
}

export function isScaStale(): boolean {
  const snapshot = sessionSnapshot();
  return snapshot ? scaStale(snapshot) : true;
}

export type ResolvedContext = {
  readonly user: User;
  readonly company: SmeCompanyView;
  readonly entitlements: EntitlementContext;
};

/**
 * Build the entitlement context for the signed-in user and selected company.
 * Returns null when the session is incomplete — callers render the auth flow.
 */
export function resolveContext(): ResolvedContext | null {
  const { userId, companyId } = useSession.getState();
  if (!userId || !companyId) return null;
  const user = db().users.find((u) => u.id === userId);
  const company = db().companies.find((c) => c.id === companyId);
  if (!user || !company) return null;
  return {
    user,
    company,
    entitlements: {
      tenant: db().tenant,
      programme: db().programme,
      company,
      user,
      featureFlags: db().featureFlags,
    },
  };
}
